import { Box, Typography, FormControlLabel, Checkbox, useTheme } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Asset, ApiSystem } from "../../state/types";
import { useAsset } from "../../context/AssetContext";
import { useSession } from "../../context/SessionContext";
import AssetStatusTag from "../assets/AssetStatusTag";
import { getMissingCameraPositions } from "../../utils/missingCameraPositions";

const normalizeSyndicationServiceName = (value: string) => value.replace(/[^a-z0-9]/gi, "").toLowerCase();

const getSyndicationMetadata = (asset: Asset) => {
  const assetWithDynamicFields = asset as Asset & Record<string, unknown>;
  const serviceName = asset.syndicationCurrentStep === "COMPLETE"
    ? asset.syndicationSteps?.[0]
    : asset.syndicationCurrentStep;

  if (!serviceName) {
    return {
      detail: "",
      flagValue: asset.syndicationProcessFlagValue,
    };
  }

  const normalizedServiceName = normalizeSyndicationServiceName(serviceName);
  const matchingProcessFlagEntry = Object.entries(assetWithDynamicFields).find(([key]) => {
    const match = key.match(/^assetSyndication_(.+)_processFlag$/);
    return !!match && normalizeSyndicationServiceName(match[1]) === normalizedServiceName;
  });

  if (!matchingProcessFlagEntry) {
    return {
      detail: "",
      flagValue: asset.syndicationProcessFlagValue,
    };
  }

  const [processFlagKey, processFlagValue] = matchingProcessFlagEntry;
  const statusValue = assetWithDynamicFields[processFlagKey.replace(/_processFlag$/, "_processFlag_status")];
  return {
    detail: typeof statusValue === "string" ? statusValue : "",
    flagValue: typeof processFlagValue === "number" ? processFlagValue : asset.syndicationProcessFlagValue,
  };
};

const getSyndicationStep = (asset: Asset) => {
  if (!asset || !asset.syndicationSteps || !asset.syndicationCurrentStep) return "";

  return asset.syndicationCurrentStep === "COMPLETE"
    ? `${asset.syndicationSteps[0]} COMPLETE`
    : asset.syndicationCurrentStep;
};

const getSyndicationTooltip = (asset: Asset) => {
  const { detail, flagValue } = getSyndicationMetadata(asset);
  if (detail && flagValue != null) return `${flagValue} - ${detail}`;
  if (detail) return detail;
  return asset.syndicationError || undefined;
};

const statusRowStyles = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexWrap: "wrap",
};

const AssetSidebar: React.FC = () => {
  const theme = useTheme();
  const { systems } = useSession()

  const {
    asset,
    visibleTypes,
    setVisibleTypes,
    imageCategories
  } = useAsset();

  if (!asset) return <></>

  const handleCheckboxChange = (key: string) => {
    setVisibleTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const missingCameraPositions = getMissingCameraPositions(asset);

  const getCommonNameForSystem = (systemCode: string) => {
    return systems?.find((sys: ApiSystem) => sys.systemCode === systemCode)?.commonName
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        position: { xs: "relative", md: "sticky" },
        top: { md: "5px" },
        height: "100%",
        borderRight: { md: `5px solid ${theme.palette.divider}` },
        borderBottom: { xs: `5px solid ${theme.palette.divider}`, md: "none" },
        overflowY: "auto",
        zIndex: 2,
        p: 2
      }}
    >
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
        Asset Info
      </Typography>

      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Ran at:</strong> {new Date(+asset.created_at * 1000).toLocaleString()}
      </Typography>
      {Array.isArray((asset as any).assetStorage_merges) && (asset as any).assetStorage_merges.length > 0 && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
            Merge History
          </Typography>

          <Box sx={{ pl: 1 }}>
            {(asset as any).assetStorage_merges
              .slice()
              .sort((a: number, b: number) => b - a) // newest first
              .map((ts: number) => (
                <Typography key={ts} variant="body2" sx={{ mb: 0.25 }}>
                  • {new Date(ts * 1000).toLocaleString()}
                </Typography>
              ))}
          </Box>
        </Box>
      )}
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Asset ID:</strong> {asset.assetId}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>System Code:</strong> {asset.systemCode}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Client Name:</strong> {getCommonNameForSystem(asset.systemCode || "")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Config Name:</strong> {asset.config}
      </Typography>
      {missingCameraPositions.length > 0 && (
        <Typography variant="body2" sx={{ mb: 1, color: "error.main" }}>
          <strong>Missing Camera Positions:</strong> {missingCameraPositions.join(", ")}
        </Typography>
      )}
      <Typography variant="body2" sx={{ mb: 1, ...statusRowStyles }}>
        <Box component="strong" sx={{ lineHeight: 1.5 }}>
          Processing Status:
        </Box>
        <AssetStatusTag
          flagValue={asset.processingProcessFlagValue}
          title={asset.processingError || undefined}
        >
          {asset.processingCurrentStep?.toUpperCase() || "NOT STARTED"}
        </AssetStatusTag>
      </Typography>

      {asset.processingError && (
        <Box
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "white",
            borderRadius: 1,
            py: 1,
            px: 2,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "1.2rem" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {asset.processingError}
          </Typography>
        </Box>
      )}

      <Typography variant="body2" sx={{ mb: 2, ...statusRowStyles }}>
        <Box component="strong" sx={{ lineHeight: 1.5 }}>
          Syndication Status:
        </Box>
        <AssetStatusTag
          flagValue={getSyndicationMetadata(asset).flagValue}
          title={getSyndicationTooltip(asset)}
        >
          {getSyndicationStep(asset)}
        </AssetStatusTag>
      </Typography>

      {asset.syndicationError && (
        <Box
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "white",
            borderRadius: 1,
            py: 1,
            px: 2,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "1.2rem" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {asset.syndicationError}
          </Typography>
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        Image Categories
      </Typography>

      {imageCategories.map(({ key, label, images }) => {
        const defaultVisible = images.length > 0;
        const isChecked = (visibleTypes[key] ?? defaultVisible);
        return (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                data-cy={`asset-sidebar-checkbox-${key}`}
                checked={isChecked}
                onChange={() => handleCheckboxChange(key)}
                disabled={!images.length}
                sx={{
                  "&.Mui-checked": {
                    color: (theme) => theme.palette.status.accent,
                  },
                }}
              />
            }
            label={`${label} (${images.length})`}
            sx={{
              opacity: images.length ? 1 : 0.5,
            }}
          />
        )
      })}
    </Box>
  );
};

export default AssetSidebar;
